import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { Calendar, Clock, ChevronRight, AlertCircle, Edit3 } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';

const SchedulePage = () => {
  const navigate = useNavigate();
  const { tournament, matches, currentMatchId, setCurrentMatchId, teams, addAnotherGame } = useAppContext();
  const [groupedMatches, setGroupedMatches] = useState({});

  // Redirect to home if no tournament
  useEffect(() => {
    if (!tournament) {
      navigate('/');
    }
  }, [tournament, navigate]);

  // Check for in-progress match and redirect to it
  useEffect(() => {
    const inProgressMatch = matches.find(match => match.status === 'in-progress');
    if (inProgressMatch) {
      setCurrentMatchId(inProgressMatch.id);
      navigate(`/match/${inProgressMatch.id}`);
    }
  }, [matches, navigate, setCurrentMatchId]);

  // Group matches by round
  useEffect(() => {
    if (!tournament) return; // Skip if no tournament

    const grouped = matches.reduce((acc, match) => {
      const round = match.round;
      if (!acc[round]) {
        acc[round] = [];
      }
      acc[round].push(match);
      return acc;
    }, {});

    setGroupedMatches(grouped);
  }, [matches, tournament]);

  // Format round name
  const formatRoundName = (round) => {
    if (round.startsWith('group-')) {
      return `Group ${round.replace('group-', '')}`;
    } else if (round.startsWith('knockout-')) {
      const roundNum = parseInt(round.replace('knockout-', ''));
      if (roundNum === 1) return 'Quarter Finals';
      if (roundNum === 2) return 'Semi Finals';
      if (roundNum === 3) return 'Final';
      return `Knockout Round ${roundNum}`;
    } else if (round === 'round-robin') {
      return 'Round Robin';
    } else if (round.startsWith('round-robin-')) {
      const roundNum = parseInt(round.replace('round-robin-', ''));
      return `Round Robin ${roundNum}`;
    } else if (round.startsWith('final-round-')) {
      const roundNum = parseInt(round.replace('final-round-', ''));
      if (roundNum === 1) return 'Quarter Finals';
      if (roundNum === 2) return 'Semi Finals';
      if (roundNum === 3) return 'Final';
      return `Finals Round ${roundNum}`;
    }
    return round;
  };

  // Get time string from minutes
  const getTimeString = (startTime) => {
    const hours = Math.floor(startTime / 60);
    const minutes = startTime % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Get match status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500';
      case 'in-progress': return 'text-blue-500';
      default: return 'text-gray-500';
    }
  };

  // Navigate to match
  const goToMatch = (matchId) => {
    setCurrentMatchId(matchId);
    navigate(`/match/${matchId}`);
  };

  // If there's no tournament, render nothing while redirecting
  if (!tournament) {
    return null;
  }

  return (
    <div className="px-4 py-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <Calendar size={24} className="mr-2" />
        Match Schedule
      </h1>

      <Card className="mb-6">
        <h2 className="text-lg font-semibold mb-1">{tournament.name}</h2>
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {tournament.format} • {matches.length} matches
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center text-sm">
            <Clock size={14} className="mr-1" />
            {tournament.eventDuration} min event, {tournament.matchDuration} min matches
          </div>

          {/* Next match button */}
          {currentMatchId && (
            <Button
              primary
              icon={<ChevronRight size={18} />}
              onClick={() => navigate(`/match/${currentMatchId}`)}
            >
              Next Match
            </Button>
          )}
        </div>
      </Card>

      {/* Match schedules by round */}
      {Object.keys(groupedMatches).length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
          <AlertCircle size={36} className="mb-2" />
          <p>No matches scheduled</p>
        </div>
      ) : (
        Object.keys(groupedMatches).sort((a, b) => {
          // Function to extract type and number from round name
          const parseRound = (round) => {
            const parts = round.split('-');
            const type = parts[0];
            const num = parseInt(parts[parts.length - 1]) || 0; // Get last part as number, default 0
            return { type, num };
          };
          
          const { type: aType, num: aNum } = parseRound(a);
          const { type: bType, num: bNum } = parseRound(b);

          // Define order for round types
          const typeOrder = { 'round': 1, 'group': 2, 'knockout': 3, 'final': 4 };
          
          // Compare types first
          if (typeOrder[aType] !== typeOrder[bType]) {
            return (typeOrder[aType] || 99) - (typeOrder[bType] || 99); // Use 99 for unknown types
          }
          
          // If types are the same, compare numbers
          return aNum - bNum;
        }).map(round => (
          <div key={round} className="mb-6">
            <h2 className="text-lg font-semibold mb-3">{formatRoundName(round)}</h2>

            <div className="space-y-3">
              {groupedMatches[round].map(match => (
                <div
                  key={match.id}
                  onClick={() => goToMatch(match.id)}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                    match.id === currentMatchId
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <Clock size={14} className="mr-1 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {getTimeString(match.startTime)}
                      </span>
                    </div>
                    <div className={`text-sm font-medium ${getStatusColor(match.status)}`}>
                      {match.status === 'completed' ? 'Completed' :
                       match.status === 'in-progress' ? 'In Progress' : 'Scheduled'}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    {/* Home Team */}
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: match.homeTeam?.color || '#ccc' }}
                      />
                      <div className="font-medium truncate">{match.homeTeam?.name || 'TBD'}</div>
                    </div>

                    {/* Score/VS */}
                    <div className="font-bold mx-2 flex-shrink-0">
                      {match.status !== 'scheduled' ? `${match.homeScore} - ${match.awayScore}` : 'vs'}
                    </div>

                    {/* Away Team */}
                    <div className="flex items-center space-x-2 flex-1 min-w-0 justify-end">
                      <div className="font-medium truncate text-right">{match.awayTeam?.name || 'TBD'}</div>
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: match.awayTeam?.color || '#ccc' }}
                      />
                    </div>
                  </div>
                  
                  {/* Edit button for completed matches */}
                  {match.status === 'completed' && (
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/50 flex justify-end">
                      <Button 
                        small 
                        tertiary 
                        icon={<Edit3 size={14} />} 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the goToMatch on the parent div
                          goToMatch(match.id);
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default SchedulePage;
